import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  FunnelChart, Funnel, LabelList, Cell,
} from 'recharts';
import jsPDF from 'jspdf';
import AdminLayout from '../components/AdminLayout';
import ExportToolbar from '../components/ExportToolbar';
import { exportCSV, exportJSON, todayStr } from '../utils/exportUtils';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Brand palette ──────────────────────────────────────────────────────
// Only Doorly's FIVE PRIMARY brand colors are used for chart fills on
// this page — blue, yellow, green, tan, red. Black is reserved for body
// text + the Closed terminal stage text (never a chart fill). No
// off-palette greys, accents, or duplicates beyond what's unavoidable
// (13 stages distributed across 5 colors).
//
//   Blue   #3a6ad9  — frontend/tailwind.config.js → 'doorly-blue'
//   Yellow #fffd5b  — frontend/tailwind.config.js → 'doorly-yellow'
//   Green  #acf44e  — used throughout app as the Doorly success
//   Tan    #FFDD86  — frontend/src/constants/statusColors.js (Option Period)
//   Red    #ff082f  — Doorly red (used app-wide for on_hold / adverse)
const BRAND = {
  blue:   '#3a6ad9',
  yellow: '#fffd5b',
  green:  '#acf44e',
  tan:    '#FFDD86',
  red:    '#ff082f',
  ink:    '#111827',   // body text only
  axisGrid: '#e5e7eb', // chart gridlines (recharts requires a value)
  axisText: '#6b7280', // axis tick labels
};

// Funnel ordering + stage color. With 5 brand colors spread across 13
// stages, semantic groupings drive the assignment:
//   yellow → entry / outbound action (Pre-Qual, Offer Requested)
//   blue   → in-progress neutral (Verification, Under Contract)
//   red    → time-critical action (In Review, Offer Submitted, Closing)
//   tan    → shopping / offer received (Shopping, Offer Accepted, Option Period)
//   green  → milestone / success (Approved, Cond. Approved, Closed)
const FUNNEL_STAGES = [
  { status: 'submitted',      label: 'Verification',    color: BRAND.blue   },
  { status: 'approved',       label: 'Approved',        color: BRAND.green  },
  { status: 'home_shopping',  label: 'Shopping',        color: BRAND.tan    },
  { status: 'offer_requested',label: 'Offer Requested', color: BRAND.yellow },
  { status: 'offer_submitted',label: 'Offer Submitted', color: BRAND.red    },
  { status: 'offer_accepted', label: 'Offer Accepted',  color: BRAND.tan    },
  { status: 'option_period',  label: 'Option Period',   color: BRAND.tan    },
  { status: 'under_contract', label: 'Under Contract',  color: BRAND.blue   },
  { status: 'closing',        label: 'Closing',         color: BRAND.red    },
  { status: 'closed',         label: 'Closed',          color: BRAND.green  },
];

const STAGE_COLOR_BY_LABEL = {
  'Pre-Qualified':    BRAND.yellow,
  'Verification':     BRAND.blue,
  'In Review':        BRAND.red,
  'Approved':         BRAND.green,
  'Cond. Approved':   BRAND.green,
  'Shopping':         BRAND.tan,
  'Offer Requested':  BRAND.yellow,
  'Offer Submitted':  BRAND.red,
  'Offer Accepted':   BRAND.tan,
  'Option Period':    BRAND.tan,
  'Under Contract':   BRAND.blue,
  'Closing':          BRAND.red,
  'Closed':           BRAND.green,
};

const PRE_QUAL_STATUSES = ['not_started', 'pre_qualified', 'new', 'started', 'registered', 'applied'];
const STATUS_LABEL = {
  not_started: 'Pre-Qualified', pre_qualified: 'Pre-Qualified', new: 'Pre-Qualified',
  started: 'Pre-Qualified', registered: 'Pre-Qualified', applied: 'Verification',
  submitted: 'Verification', in_review: 'In Review', approved: 'Approved',
  conditional_approval: 'Cond. Approved', home_shopping: 'Shopping',
  offer_requested: 'Offer Requested', offer_submitted: 'Offer Submitted',
  offer_accepted: 'Offer Accepted', option_period: 'Option Period',
  // Added so the under_contract bucket (Jane Smith et al.) shows on both
  // 'Borrowers by Stage' and 'Avg Days to Reach Stage' — previously missing
  // entries dropped the row from the rendered list entirely.
  under_contract: 'Under Contract',
  closing: 'Closing', clear_to_close: 'Closing', closed: 'Closed', funded: 'Closed',
  // on_hold + not_moving_forward are excluded server-side, no label needed
};

// Canonical stage order so charts read left-to-right (or top-to-bottom)
// in pipeline sequence, not by data count. Falls back to alphabetical
// when a status isn't in this list.
const STAGE_ORDER = [
  'Pre-Qualified','Verification','In Review','Approved','Cond. Approved',
  'Shopping','Offer Requested','Offer Submitted','Offer Accepted',
  'Option Period','Under Contract','Closing','Closed',
];
const stageOrderIndex = (label) => {
  const i = STAGE_ORDER.indexOf(label);
  return i === -1 ? 999 : i;
};
// Color resolver — pulls from STAGE_COLOR_BY_LABEL so every bar across
// every chart uses one of the four primary brand colors. Falls back to
// blue if a label slips in that we haven't mapped (defensive).
const stageColor = (label) => STAGE_COLOR_BY_LABEL[label] || BRAND.blue;

const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: `1px solid ${BRAND.axisGrid}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };

const QUICK_RANGES = [
  { label: 'All Time', days: null },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 90d', days: 90 },
  { label: 'Last 6mo', days: 180 },
  { label: 'Last 12mo', days: 365 },
];

const fmt$ = (v) => (v == null || Number.isNaN(Number(v)) ? '$0' : '$' + Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const fmtCompact$ = (v) => {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + 'M';
  if (n >= 1_000)     return '$' + (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + 'K';
  return '$' + n.toFixed(0);
};

export default function AdminReportsPipeline() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [statusCounts, setStatusCounts] = useState([]);
  const [volumeByStage, setVolumeByStage] = useState([]);
  const [mtd, setMtd] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalVolume, setTotalVolume] = useState(0);
  const [avgDays, setAvgDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeQuick, setActiveQuick] = useState('All Time');

  const load = useCallback(async (sd, ed) => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (sd && ed) { params.start_date = sd; params.end_date = ed; }
      const res = await axios.get(`${API}/api/admin/reports/pipeline`, { headers, params });
      setStatusCounts(res.data.status_counts || []);
      setVolumeByStage(res.data.volume_by_stage || []);
      setMtd(res.data.mtd || 0);
      setTotal(res.data.total || 0);
      setTotalVolume(Number(res.data.total_volume || 0));
      setAvgDays(res.data.avg_days || []);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(err?.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load('', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyQuick = (q) => {
    setActiveQuick(q.label);
    if (!q.days) { setStartDate(''); setEndDate(''); load('', ''); return; }
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - q.days * 86400000).toISOString().slice(0, 10);
    setStartDate(start); setEndDate(end); load(start, end);
  };

  // Sum counts across a list of statuses (used for funnel rollups + KPI math)
  const getCount = (statuses) => {
    const arr = Array.isArray(statuses) ? statuses : [statuses];
    return statusCounts.filter((s) => arr.includes(s.status)).reduce((sum, s) => sum + Number(s.count), 0);
  };
  const getVolume = (statuses) => {
    const arr = Array.isArray(statuses) ? statuses : [statuses];
    return volumeByStage.filter((s) => arr.includes(s.status)).reduce((sum, s) => sum + Number(s.volume), 0);
  };

  const funnelData = [
    { label: 'Pre-Qualified', count: getCount(PRE_QUAL_STATUSES), color: BRAND.yellow },
    ...FUNNEL_STAGES.map((s) => ({ label: s.label, count: getCount(s.status), color: s.color })),
  ].filter((f) => f.count > 0);

  // Borrowers by Stage — sorted by canonical pipeline order, not count,
  // so a viewer reads the chart the same way a borrower walks the funnel.
  const barData = statusCounts
    .map((s) => ({ status: STATUS_LABEL[s.status] || s.status, count: Number(s.count) }))
    .reduce((acc, row) => {
      // Collapse synonymous labels (e.g. multiple Pre-Qualified statuses) into one bar
      const existing = acc.find((r) => r.status === row.status);
      if (existing) existing.count += row.count; else acc.push(row);
      return acc;
    }, [])
    .sort((a, b) => stageOrderIndex(a.status) - stageOrderIndex(b.status));

  // Loan Volume by Stage — every active stage that has at least one
  // borrower shows up, even if its loan-amount sum is $0 (early-stage
  // borrowers haven't been priced through Atlas yet). Volume is loan
  // AMOUNT (loan principal), not purchase price — backend uses
  // contract × (1−dp_pct) → atlas_max_loan_amount → max_approval_amount.
  // Stages with NO borrowers at all stay out so the chart isn't padded
  // with empty rows for stages no loan is sitting in.
  const stagesWithLoans = new Set(barData.map((r) => r.status));
  const volumeData = volumeByStage
    .map((s) => ({ status: STATUS_LABEL[s.status] || s.status, volume: Number(s.volume) }))
    .reduce((acc, row) => {
      const existing = acc.find((r) => r.status === row.status);
      if (existing) existing.volume += row.volume; else acc.push(row);
      return acc;
    }, [])
    .filter((r) => stagesWithLoans.has(r.status))
    .sort((a, b) => stageOrderIndex(a.status) - stageOrderIndex(b.status));

  // Avg Days to Reach Stage — backend now returns rows shaped
  // { stage: 'Verification', avg_days: 5.2, n: 10 } where each borrower
  // contributes to EVERY stage they've reached (current + past),
  // computed via stage-specific transition timestamps. Pre-Qualified
  // is omitted from the backend result entirely (registration is the
  // initial state, ~0 days). Frontend just sorts into pipeline order.
  const avgDaysData = avgDays
    .filter((d) => d.avg_days != null && d.stage)
    .map((d) => ({ status: d.stage, days: parseFloat(d.avg_days), n: Number(d.n) || 0 }))
    .sort((a, b) => stageOrderIndex(a.status) - stageOrderIndex(b.status));

  // KPIs
  // 'Approved' counts every loan file that has REACHED approval — currently
  // in approved/conditional_approval OR any downstream stage. Previously
  // this read 'currently approved', which dropped to 0 the moment a
  // borrower advanced to Home Shopping, even though they were still
  // approved. The full downstream list is the same one used to filter
  // 'reached this stage' anywhere else in the pipeline report.
  const APPROVED_OR_LATER = [
    'approved', 'conditional_approval',
    'home_shopping',
    'offer_requested', 'offer_submitted', 'offer_accepted',
    'option_period', 'under_contract',
    'closing', 'clear_to_close',
    'closed', 'funded',
  ];
  const approved = getCount(APPROVED_OR_LATER);
  const closed = getCount(['closed', 'funded']);
  const closedVolume = getVolume(['closed', 'funded']);

  // Total avg days registration → Closed (the most advanced stage in the
  // funnel). Null when no Closed data exists yet; the chart renders a
  // fallback message in that case.
  const totalDaysToClosed = avgDaysData.find((r) => r.status === 'Closed')?.days ?? null;

  // ── PDF export ──────────────────────────────────────────────────────
  // jsPDF document with a Doorly-branded header (logo + title +
  // generated timestamp) and a Go Doorly LLC footer. Uses the table
  // data already on this page — re-renders into the PDF as text rows
  // rather than rasterizing the live charts (smaller file, sharper
  // text, and no html2canvas dependency).
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const M = 48;

      // ─ Load logo as base64 data URI + capture its native dimensions
      //   so the header renders with the correct aspect ratio. The
      //   previous render assumed a 4.75:1 wordmark, which stretched
      //   the actual file (it's closer to 3.3:1) horizontally.
      const logoMeta = await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            ratio: img.naturalWidth / img.naturalHeight,
          });
        };
        img.onerror = () => resolve(null);
        img.src = `${process.env.PUBLIC_URL || ''}/doorly_logo_black.png`;
      });

      const generatedAt = new Date().toLocaleString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      });

      const drawHeader = () => {
        if (logoMeta?.dataUrl) {
          // Aspect-preserved: width derived from the file's natural ratio.
          const h = 22;
          const w = h * logoMeta.ratio;
          pdf.addImage(logoMeta.dataUrl, 'PNG', M, M - 12, w, h);
        } else {
          pdf.setFont('helvetica', 'bold').setFontSize(16).setTextColor(BRAND.ink);
          pdf.text('Doorly', M, M);
        }
        pdf.setFont('helvetica', 'normal').setFontSize(8).setTextColor(BRAND.axisText);
        pdf.text(`Generated ${generatedAt}`, pageW - M, M - 4, { align: 'right' });
        pdf.setDrawColor(220).setLineWidth(0.5).line(M, M + 18, pageW - M, M + 18);
      };

      const drawFooter = () => {
        const footerY = pageH - 28;
        pdf.setDrawColor(220).setLineWidth(0.5).line(M, footerY - 14, pageW - M, footerY - 14);
        pdf.setFont('helvetica', 'normal').setFontSize(8).setTextColor(BRAND.axisText);
        pdf.text('Go Doorly, LLC  ·  NMLS #2764749  ·  Equal Housing Opportunity  ·  support@godoorly.com', M, footerY, { align: 'left' });
        pdf.text(`Page ${pdf.internal.getCurrentPageInfo().pageNumber}`, pageW - M, footerY, { align: 'right' });
      };

      drawHeader();
      let y = M + 40;

      // ─ Report title ──────────────────────────────────────────────
      pdf.setFont('helvetica', 'bold').setFontSize(18).setTextColor(BRAND.ink);
      pdf.text('Pipeline Report', M, y); y += 18;
      pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(BRAND.axisText);
      const rangeLabel = (startDate && endDate)
        ? `${startDate} → ${endDate}`
        : (activeQuick || 'All Time');
      pdf.text(`Range: ${rangeLabel}    Excludes: on-hold, not moving forward, co-borrowers`, M, y);
      y += 24;

      // ─ KPI strip ─────────────────────────────────────────────────
      pdf.setFont('helvetica', 'bold').setFontSize(11).setTextColor(BRAND.ink);
      pdf.text('Headline KPIs', M, y); y += 12;
      const kpis = [
        ['Total Loan Files', total.toString()],
        ['Total Loan Volume', fmt$(totalVolume)],
        ['Applications MTD', mtd.toString()],
        ['Approved', approved.toString()],
        ['Funded / Closed', closed.toString()],
        ['Funded / Closed Volume', fmt$(closedVolume)],
      ];
      pdf.setFont('helvetica', 'normal').setFontSize(10);
      kpis.forEach(([k, v]) => {
        pdf.setTextColor(BRAND.axisText).text(k, M, y);
        pdf.setTextColor(BRAND.ink).setFont('helvetica', 'bold').text(v, M + 200, y);
        pdf.setFont('helvetica', 'normal');
        y += 14;
      });
      y += 10;

      // ─ Helper: table ─────────────────────────────────────────────
      const drawTable = (title, rows, headers) => {
        if (y > pageH - 100) { drawFooter(); pdf.addPage(); drawHeader(); y = M + 40; }
        pdf.setFont('helvetica', 'bold').setFontSize(11).setTextColor(BRAND.ink);
        pdf.text(title, M, y); y += 10;
        pdf.setDrawColor(220).setLineWidth(0.5).line(M, y, pageW - M, y); y += 12;
        const col1X = M;
        const col2X = pageW - M;
        pdf.setFontSize(9).setTextColor(BRAND.axisText).setFont('helvetica', 'bold');
        pdf.text(headers[0], col1X, y);
        pdf.text(headers[1], col2X, y, { align: 'right' });
        y += 6; pdf.setDrawColor(235).line(M, y, pageW - M, y); y += 12;
        pdf.setFont('helvetica', 'normal').setTextColor(BRAND.ink);
        rows.forEach(([a, b]) => {
          if (y > pageH - 60) { drawFooter(); pdf.addPage(); drawHeader(); y = M + 40; }
          pdf.text(String(a), col1X, y);
          pdf.text(String(b), col2X, y, { align: 'right' });
          y += 14;
        });
        y += 8;
      };

      drawTable(
        'Borrowers by Stage',
        barData.length ? barData.map((r) => [r.status, r.count.toString()]) : [['No data', '—']],
        ['Stage', 'Loan Count']
      );
      drawTable(
        'Loan Volume by Stage',
        volumeData.length ? volumeData.map((r) => [r.status, fmt$(r.volume)]) : [['No data', '$0']],
        ['Stage', 'Loan Volume']
      );
      drawTable(
        'Avg Days to Reach Stage',
        avgDaysData.length
          ? [
              // Lead row — total registration → Closed, matches the
              // banner on the live page. Falls back to em-dash when
              // no Closed loans exist.
              [
                'Registration → Closed (total)',
                totalDaysToClosed != null ? totalDaysToClosed.toFixed(1) + ' days' : '—',
              ],
              ...avgDaysData.map((r) => [r.status, r.days.toFixed(1) + ' days']),
            ]
          : [['No data', '—']],
        ['Stage', 'Avg Days']
      );

      drawFooter();
      pdf.save(`doorly-pipeline-report-${todayStr()}.pdf`);
    } catch (e) {
      console.error('[Pipeline PDF export]', e);
      setError(e.message || 'PDF export failed.');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: BRAND.ink, marginBottom: '4px' }}>Pipeline Reports</h1>
            <p style={{ fontSize: '13px', color: BRAND.axisText }}>
              Application funnel + stage distribution &nbsp;·&nbsp;
              <span style={{ color: '#9ca3af' }}>Excludes on-hold, not-moving-forward, co-borrowers</span>
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <ExportToolbar
              disabled={statusCounts.length === 0}
              onExportCSV={() => exportCSV(
                statusCounts,
                [
                  { label: 'Stage', accessor: (r) => STATUS_LABEL[r.status] || r.status },
                  { label: 'Loan Count', key: 'count' },
                ],
                `doorly-pipeline-report-${todayStr()}`
              )}
              onExportJSON={() => exportJSON(
                { report: 'Pipeline', generated: new Date().toISOString(), status_counts: statusCounts, volume_by_stage: volumeByStage, total_volume: totalVolume, avg_days: avgDays },
                `doorly-pipeline-report-${todayStr()}`
              )}
            />
            <button onClick={handleExportPdf} disabled={statusCounts.length === 0 || exportingPdf}
              style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: exportingPdf ? 'not-allowed' : 'pointer',
                background: BRAND.blue, color: '#fff', border: 'none', opacity: (statusCounts.length === 0 || exportingPdf) ? 0.5 : 1 }}>
              {exportingPdf ? 'Exporting…' : 'Export PDF'}
            </button>
            {QUICK_RANGES.map((q) => (
              <button key={q.label} onClick={() => applyQuick(q)}
                style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid',
                  backgroundColor: activeQuick === q.label ? BRAND.ink : '#fff',
                  color: activeQuick === q.label ? '#fff' : '#374151',
                  borderColor: activeQuick === q.label ? BRAND.ink : BRAND.axisGrid }}>
                {q.label}
              </button>
            ))}
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setActiveQuick(''); }}
              style={{ padding: '6px 10px', borderRadius: '8px', border: `1px solid ${BRAND.axisGrid}`, fontSize: '13px' }} />
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>to</span>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setActiveQuick(''); }}
              style={{ padding: '6px 10px', borderRadius: '8px', border: `1px solid ${BRAND.axisGrid}`, fontSize: '13px' }} />
            <button onClick={() => { setActiveQuick(''); load(startDate, endDate); }}
              style={{ padding: '6px 14px', borderRadius: '8px', backgroundColor: BRAND.blue, color: '#fff', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              Apply
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '80px', textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#ff082f' }}>{error}</div>
        ) : (
          <>
            {/* KPI Cards — six cards across, counts AND dollar volume */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Active Loan Files',         value: total },
                { label: 'Total Loan Volume',         value: fmt$(totalVolume), accent: true },
                { label: 'Applications MTD',          value: mtd },
                { label: 'Approved',                  value: approved },
                { label: 'Funded / Closed',           value: closed },
                { label: 'Funded / Closed Volume',    value: fmt$(closedVolume), accent: true },
              ].map((k) => (
                <div key={k.label} style={{ ...card, padding: '14px 16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: BRAND.axisText, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{k.label}</div>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: k.accent ? BRAND.blue : BRAND.ink, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              {/* Funnel visualization */}
              <div style={card}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: BRAND.ink, marginBottom: '16px' }}>Pipeline Funnel</h3>
                {funnelData.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px', fontSize: '13px' }}>No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <FunnelChart>
                      <Tooltip formatter={(v, name) => [v + ' loan files', name]} />
                      <Funnel dataKey="count" data={funnelData} isAnimationActive>
                        {funnelData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        <LabelList position="right" content={({ value, name }) => (
                          <text fill="#374151" fontSize={12} fontWeight={500}>{name}: {value}</text>
                        )} />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Count by status bar chart */}
              <div style={card}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: BRAND.ink, marginBottom: '16px' }}>Borrowers by Stage</h3>
                {barData.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px', fontSize: '13px' }}>No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart layout="vertical" data={barData} margin={{ left: 0, right: 24, top: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={BRAND.axisGrid} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="status" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [v + ' loan files', 'Count']} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {barData.map((entry, i) => <Cell key={i} fill={stageColor(entry.status)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Loan Volume by Stage — full-width row */}
            <div style={{ ...card, marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: BRAND.ink, marginBottom: '16px' }}>Loan Volume by Stage</h3>
              {volumeData.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px', fontSize: '13px' }}>
                  No volume yet — borrowers gain a loan amount once they're priced through Atlas or sign a contract.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, 44 * volumeData.length + 40)}>
                  <BarChart layout="vertical" data={volumeData} margin={{ left: 0, right: 64, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={BRAND.axisGrid} />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtCompact$} />
                    <YAxis type="category" dataKey="status" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [fmt$(v), 'Loan Volume']} />
                    <Bar dataKey="volume" radius={[0, 4, 4, 0]}>
                      {volumeData.map((entry, i) => <Cell key={i} fill={stageColor(entry.status)} />)}
                      <LabelList dataKey="volume" position="right" formatter={(v) => fmtCompact$(v)}
                        style={{ fontSize: 11, fontWeight: 700, fill: BRAND.ink }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Avg days to stage */}
            {avgDaysData.length > 0 && (
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: BRAND.ink, margin: '0 0 4px' }}>Avg Days to Reach Stage</h3>
                    <p style={{ fontSize: '11px', color: BRAND.axisText, margin: 0 }}>
                      Each borrower contributes to every stage they've reached (current OR past). Pre-Qualified is the registration entry point (~0 d) so it's not charted.
                    </p>
                  </div>
                  {/* Total banner — registration → Closed, the canonical
                      end-to-end pipeline time. Renders muted when no
                      Closed data exists yet so an empty state doesn't
                      pretend to have a meaningful summary. */}
                  <div style={{
                    background: totalDaysToClosed != null ? '#f0fdf4' : '#f3f4f6',
                    border: `1px solid ${totalDaysToClosed != null ? '#bbf7d0' : '#e5e7eb'}`,
                    borderRadius: 10,
                    padding: '10px 16px',
                    minWidth: 200,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.axisText, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                      Total: Registration → Closed
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: totalDaysToClosed != null ? '#166534' : BRAND.axisText, fontVariantNumeric: 'tabular-nums' }}>
                      {totalDaysToClosed != null ? `${totalDaysToClosed.toFixed(1)} days` : '—'}
                    </div>
                    {totalDaysToClosed == null && (
                      <div style={{ fontSize: 10, color: BRAND.axisText, marginTop: 2 }}>
                        No closed loans yet
                      </div>
                    )}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={Math.max(200, 44 * avgDaysData.length + 40)}>
                  <BarChart layout="vertical" data={avgDaysData} margin={{ left: 0, right: 80, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={BRAND.axisGrid} />
                    <XAxis type="number" tick={{ fontSize: 11 }} unit=" days" allowDecimals={true} />
                    <YAxis type="category" dataKey="status" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const row = payload[0].payload;
                        return (
                          <div style={{ background: '#fff', border: `1px solid ${BRAND.axisGrid}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                            <div style={{ fontWeight: 700, color: BRAND.ink, marginBottom: 2 }}>{row.status}</div>
                            <div style={{ color: BRAND.axisText }}>Avg: <strong style={{ color: BRAND.ink }}>{Number(row.days).toFixed(1)} days</strong></div>
                            <div style={{ color: BRAND.axisText }}>Across {row.n} borrower{row.n === 1 ? '' : 's'}</div>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="days" radius={[0, 4, 4, 0]} minPointSize={2}>
                      {avgDaysData.map((entry, i) => <Cell key={i} fill={stageColor(entry.status)} />)}
                      {/* Always render the day count to the right of every
                          bar — without this label, near-zero bars (e.g. the
                          Approved row when status_updated_at ≈ created_at)
                          render as an invisible sliver with no value cue. */}
                      <LabelList dataKey="days" position="right"
                        formatter={(v) => Number(v).toFixed(1) + ' d'}
                        style={{ fontSize: 11, fontWeight: 700, fill: BRAND.ink }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
